import React, { useState, useEffect, useRef } from 'react';
import './GameBoard.css';
import ColorBlock from './ColorBlock';
import ParticleEffect from './ParticleEffect';
import { calculateScore, getDifficultySettings } from '../utils/gameUtils';

const COLORS = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];
const BOARD_SIZE = { rows: 8, cols: 6 }; // 모바일에 최적화된 크기

const GameBoard = ({ gameMode, updateScore, endGame }) => {
  const [board, setBoard] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [combo, setCombo] = useState(0);
  const [lastMatchTime, setLastMatchTime] = useState(Date.now());
  const [level, setLevel] = useState(1);
  const [particles, setParticles] = useState([]);
  const gameboardRef = useRef(null);

  // 게임 보드 초기화
  useEffect(() => {
    initializeBoard();
  }, []);

  // 레벨 업 시스템 (아케이드 모드)
  useEffect(() => {
    if (gameMode === 'arcade') {
      const levelUpScore = level * 500; // 레벨당 500점씩 증가
      if (combo >= 5 && combo % 5 === 0) {
        setLevel(prevLevel => prevLevel + 1);
      }
    }
  }, [combo, gameMode, level]);

  // 콤보 시스템 (3초 이내에 매치하면 콤보 유지, 아니면 리셋)
  useEffect(() => {
    if (selectedBlocks.length === 0 && combo > 0) {
      const comboTimeout = setTimeout(() => {
        if (Date.now() - lastMatchTime > 3000) {
          setCombo(0);
        }
      }, 3000);
      
      return () => clearTimeout(comboTimeout);
    }
  }, [selectedBlocks, combo, lastMatchTime]);

  // 게임 보드 초기화 함수
  const initializeBoard = () => {
    const difficultySettings = getDifficultySettings(level);
    const newBoard = [];
    
    for (let row = 0; row < BOARD_SIZE.rows; row++) {
      const newRow = [];
      for (let col = 0; col < BOARD_SIZE.cols; col++) {
        // 랜덤 색상 블록 생성 (난이도에 따라 특수 블록 확률 조정)
        const isSpecial = Math.random() < difficultySettings.specialBlockChance;
        const color = COLORS[Math.floor(Math.random() * COLORS.length)];
        newRow.push({
          id: `${row}-${col}`,
          color,
          isSpecial,
          type: isSpecial ? (Math.random() < 0.5 ? 'bomb' : 'rainbow') : 'normal'
        });
      }
      newBoard.push(newRow);
    }
    
    setBoard(newBoard);
  };

  // 블록 선택 처리
  const handleBlockSelect = (row, col) => {
    const currentBlock = board[row][col];
    
    // 이미 선택된 블록이면 선택 취소
    if (selectedBlocks.some(block => block.row === row && block.col === col)) {
      setSelectedBlocks([]);
      return;
    }
    
    // 첫 번째 블록 선택
    if (selectedBlocks.length === 0) {
      setSelectedBlocks([{ row, col, color: currentBlock.color, type: currentBlock.type }]);
      return;
    }
    
    // 마지막으로 선택된 블록
    const lastBlock = selectedBlocks[selectedBlocks.length - 1];
    
    // 인접한 블록인지 확인 (상하좌우)
    const isAdjacent = 
      (Math.abs(row - lastBlock.row) === 1 && col === lastBlock.col) ||
      (Math.abs(col - lastBlock.col) === 1 && row === lastBlock.row);
    
    // 레인보우 블록은 어떤 색상과도 매칭 가능
    const isSameColor = 
      currentBlock.type === 'rainbow' || 
      lastBlock.type === 'rainbow' || 
      currentBlock.color === lastBlock.color;
    
    if (isAdjacent && isSameColor) {
      setSelectedBlocks([...selectedBlocks, { row, col, color: currentBlock.color, type: currentBlock.type }]);
    } else {
      // 조건에 맞지 않으면 선택 리셋
      setSelectedBlocks([{ row, col, color: currentBlock.color, type: currentBlock.type }]);
    }
  };

  // 선택된 블록 제거 및 점수 계산
  const removeSelectedBlocks = () => {
    if (selectedBlocks.length < 3) {
      setSelectedBlocks([]);
      return;
    }
    
    // 새 보드 생성
    const newBoard = [...board];
    
    // 폭탄 블록이 있는지 확인
    const hasBomb = selectedBlocks.some(block => board[block.row][block.col].type === 'bomb');
    
    // 파티클 효과를 위한 위치 저장
    const particlePositions = [];
    
    // 선택된 블록 제거
    selectedBlocks.forEach(block => {
      if (gameboardRef.current) {
        const blockElement = document.querySelector(`.color-block[data-row="${block.row}"][data-col="${block.col}"]`);
        if (blockElement) {
          const rect = blockElement.getBoundingClientRect();
          const gameboardRect = gameboardRef.current.getBoundingClientRect();
          
          particlePositions.push({
            x: rect.left - gameboardRect.left + rect.width / 2,
            y: rect.top - gameboardRect.top + rect.height / 2,
            color: block.color
          });
        }
      }
      
      newBoard[block.row][block.col] = null;
    });
    
    // 파티클 효과 생성
    setParticles(particlePositions);
    
    // 폭탄 블록이 있으면 주변 블록도 제거
    if (hasBomb) {
      selectedBlocks.forEach(block => {
        if (board[block.row][block.col].type === 'bomb') {
          // 폭탄 주변 3x3 영역의 블록 제거
          for (let r = Math.max(0, block.row - 1); r <= Math.min(BOARD_SIZE.rows - 1, block.row + 1); r++) {
            for (let c = Math.max(0, block.col - 1); c <= Math.min(BOARD_SIZE.cols - 1, block.col + 1); c++) {
              if (newBoard[r][c] && gameboardRef.current) {
                const blockElement = document.querySelector(`.color-block[data-row="${r}"][data-col="${c}"]`);
                if (blockElement) {
                  const rect = blockElement.getBoundingClientRect();
                  const gameboardRect = gameboardRef.current.getBoundingClientRect();
                  
                  particlePositions.push({
                    x: rect.left - gameboardRect.left + rect.width / 2,
                    y: rect.top - gameboardRect.top + rect.height / 2,
                    color: newBoard[r][c].color
                  });
                }
              }
              
              newBoard[r][c] = null;
            }
          }
        }
      });
    }
    
    // 빈 공간 채우기
    for (let col = 0; col < BOARD_SIZE.cols; col++) {
      let emptySpaces = 0;
      
      // 아래에서 위로 검사하며 빈 공간 카운트
      for (let row = BOARD_SIZE.rows - 1; row >= 0; row--) {
        if (newBoard[row][col] === null) {
          emptySpaces++;
        } else if (emptySpaces > 0) {
          // 블록을 아래로 이동
          newBoard[row + emptySpaces][col] = newBoard[row][col];
          newBoard[row][col] = null;
        }
      }
      
      // 맨 위에 새 블록 생성
      const difficultySettings = getDifficultySettings(level);
      for (let row = 0; row < emptySpaces; row++) {
        const isSpecial = Math.random() < difficultySettings.specialBlockChance;
        const color = COLORS[Math.floor(Math.random() * COLORS.length)];
        newBoard[row][col] = {
          id: `new-${row}-${col}-${Date.now()}`,
          color,
          isSpecial,
          type: isSpecial ? (Math.random() < 0.5 ? 'bomb' : 'rainbow') : 'normal'
        };
      }
    }
    
    // 점수 계산 (기본 점수 + 콤보 보너스 + 특수 블록 보너스)
    const hasSpecialBlocks = selectedBlocks.some(block => 
      board[block.row][block.col].type === 'bomb' || 
      board[block.row][block.col].type === 'rainbow'
    );
    
    const totalPoints = calculateScore(selectedBlocks.length, combo, hasSpecialBlocks);
    
    // 콤보 증가 및 마지막 매치 시간 업데이트
    setCombo(combo + 1);
    setLastMatchTime(Date.now());
    
    // 점수 업데이트
    updateScore(totalPoints);
    
    // 보드 업데이트 및 선택 초기화
    setBoard(newBoard);
    setSelectedBlocks([]);
  };

  // 블록 확정 (3개 이상 선택 시)
  useEffect(() => {
    if (selectedBlocks.length >= 3) {
      const confirmTimeout = setTimeout(() => {
        removeSelectedBlocks();
      }, 300); // 0.3초 후 자동 확정
      
      return () => clearTimeout(confirmTimeout);
    }
  }, [selectedBlocks]);

  return (
    <div className="game-board" ref={gameboardRef}>
      {board.map((row, rowIndex) => (
        <div key={`row-${rowIndex}`} className="board-row">
          {row.map((block, colIndex) => (
            <ColorBlock
              key={block ? block.id : `empty-${rowIndex}-${colIndex}`}
              color={block ? block.color : 'transparent'}
              type={block ? block.type : 'normal'}
              isSelected={selectedBlocks.some(b => b.row === rowIndex && b.col === colIndex)}
              onClick={() => block && handleBlockSelect(rowIndex, colIndex)}
              data-row={rowIndex}
              data-col={colIndex}
            />
          ))}
        </div>
      ))}
      
      {particles.map((particle, index) => (
        <ParticleEffect
          key={`particle-${index}-${Date.now()}`}
          color={particle.color}
          x={particle.x}
          y={particle.y}
          gameboardRef={gameboardRef}
        />
      ))}
      
      <div className="combo-display">
        {combo > 1 && <span className="combo-text">콤보 x{combo}</span>}
      </div>
      
      {gameMode === 'arcade' && (
        <div className="level-display">
          <span className="level-text">레벨 {level}</span>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
